<script>
  import { router } from '@inertiajs/svelte'
  
  let { post = {}, versions = [], reports = [], flash = {} } = $props()
  
  let rejectNote = $state('')
  let showRejectModal = $state(false)
  let showBlindModal = $state(false)
  let blindReason = $state('')
  let blindDuration = $state(30)
  let blindRequestedBy = $state('admin')
  
  function approvePost() {
    if (confirm('이 게시글을 승인하시겠습니까?')) {
      router.post(`/admin/posts/${post.id}/approve`)
    }
  }
  
  function rejectPost() {
    router.post(`/admin/posts/${post.id}/reject`, { note: rejectNote })
    showRejectModal = false
  }
  
  function flagPost() {
    if (confirm('이 게시글을 플래그하시겠습니까?')) {
      router.post(`/admin/posts/${post.id}/flag`)
    }
  }

  function blindPost() {
    router.post(`/admin/posts/${post.id}/blind`, {
      reason: blindReason || '임시조치 (정보통신망법 제44조의2)',
      duration_days: blindDuration,
      requested_by: blindRequestedBy
    })
    showBlindModal = false
  }

  function unblindPost() {
    if (confirm('이 게시글의 블라인드를 해제하시겠습니까?')) {
      router.post(`/admin/posts/${post.id}/unblind`)
    }
  }
  
  function getStatusBadge(status) {
    switch (status) {
      case 'approved': return 'badge-success'
      case 'pending': return 'badge-info'
      case 'flagged': return 'badge-warning'
      case 'rejected': return 'badge-error'
      default: return 'badge-neutral'
    }
  }
</script>

<!-- Breadcrumb -->
  <div class="flex items-center gap-2 text-caption text-content-tertiary mb-6">
    <a href="/admin" class="hover:text-primary transition-colors">관리자</a>
    <span>/</span>
    <a href="/admin/posts" class="hover:text-primary transition-colors">게시글</a>
    <span>/</span>
    <span class="text-content truncate max-w-xs">{post.title}</span>
  </div>

  <main class="max-w-4xl">
    {#if flash.notice}
      <div class="mb-6 p-4 bg-success-light dark:bg-success/20 border border-success/30 rounded-xl text-success-dark dark:text-success text-body2">
        {flash.notice}
      </div>
    {/if}
    
    <!-- Post Info -->
    <div class="card card-bordered overflow-hidden mb-6 shadow-md">
      <div class="p-6 border-b border-default">
        <div class="flex items-start justify-between">
          <div>
            <div class="flex items-center gap-2 mb-2">
              <span class={`badge ${getStatusBadge(post.moderation_status)}`}>
                {post.moderation_status || 'pending'}
              </span>
              <span class="text-caption text-content-tertiary">{post.board?.name}</span>
            </div>
            <h1 class="text-h1 text-content">{post.title}</h1>
          </div>
          
          <!-- Actions -->
          <div class="flex items-center gap-2">
            {#if post.moderation_status !== 'approved'}
              <button onclick={approvePost} class="btn btn-primary btn-sm">승인</button>
            {/if}
            {#if post.moderation_status !== 'rejected'}
              <button onclick={() => showRejectModal = true} class="btn btn-danger btn-sm">반려</button>
            {/if}
            {#if post.moderation_status !== 'flagged'}
              <button onclick={flagPost} class="btn btn-secondary btn-sm">플래그</button>
            {/if}
            {#if post.is_blinded}
              <button onclick={unblindPost} class="btn btn-primary btn-sm">블라인드 해제</button>
            {:else}
              <button onclick={() => showBlindModal = true} class="btn btn-secondary btn-sm">임시 블라인드</button>
            {/if}
          </div>
        </div>
      </div>
      
      <!-- Author Info -->
      <div class="p-6 border-b border-default bg-surface-tertiary">
        <div class="flex items-center justify-between">
          <div class="flex items-center gap-3">
            <div class="w-10 h-10 bg-primary-50 dark:bg-dark-elevated rounded-full flex items-center justify-center text-primary dark:text-dark-text-high font-semibold">
              {post.user?.name?.charAt(0) || '?'}
            </div>
            <div>
              <div class="text-body1 font-medium text-content">{post.user?.name || '익명'}</div>
              <div class="text-caption text-content-tertiary">{post.user?.email}</div>
            </div>
          </div>
          <div class="text-right text-caption text-content-tertiary">
            <div>작성: {new Date(post.created_at).toLocaleString('ko-KR')}</div>
            <div>수정: {new Date(post.updated_at).toLocaleString('ko-KR')}</div>
          </div>
        </div>
      </div>
      
      <!-- Content -->
      <div class="p-6">
        <div class="text-body2 text-content whitespace-pre-wrap">
          {post.content}
        </div>
      </div>
      
      <!-- Stats -->
      <div class="p-6 border-t border-default bg-surface-tertiary">
        <div class="flex flex-wrap items-center gap-6 text-body2">
          <span class="text-content-secondary">조회 <span class="text-content font-semibold">{post.views_count || 0}</span></span>
          <span class="text-content-secondary">댓글 <span class="text-content font-semibold">{post.comments_count || 0}</span></span>
          <span class="text-content-secondary">추천 <span class="text-content font-semibold">{post.likes_count || 0}</span></span>
          {#if post.is_pinned}
            <span class="badge badge-warning">고정됨</span>
          {/if}
          {#if post.is_notice}
            <span class="badge badge-error">공지</span>
          {/if}
        </div>
      </div>
      
      <!-- Blind Status (임시조치) -->
      {#if post.is_blinded}
        <div class="p-6 border-t border-orange-200 dark:border-orange-800 bg-orange-50 dark:bg-orange-900/20">
          <div class="flex items-center gap-2 mb-3">
            <svg class="w-5 h-5 text-orange-600 dark:text-orange-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
            </svg>
            <h3 class="text-label text-orange-700 dark:text-orange-400 uppercase font-bold">임시 블라인드 처리됨 (임시조치)</h3>
          </div>
          <dl class="space-y-2 text-body2">
            <div class="flex">
              <dt class="text-orange-600 dark:text-orange-400 w-28">처리일</dt>
              <dd class="text-content">{post.blinded_at ? new Date(post.blinded_at).toLocaleString('ko-KR') : '-'}</dd>
            </div>
            <div class="flex">
              <dt class="text-orange-600 dark:text-orange-400 w-28">만료일</dt>
              <dd class="text-content">{post.blind_expires_at ? new Date(post.blind_expires_at).toLocaleString('ko-KR') : '무기한'}</dd>
            </div>
            <div class="flex">
              <dt class="text-orange-600 dark:text-orange-400 w-28">사유</dt>
              <dd class="text-content">{post.blind_reason || '-'}</dd>
            </div>
            <div class="flex">
              <dt class="text-orange-600 dark:text-orange-400 w-28">요청자</dt>
              <dd class="text-content">{post.blind_requested_by === 'user' ? '사용자 신고' : '관리자 판단'}</dd>
            </div>
          </dl>
          <p class="mt-3 text-caption text-orange-600 dark:text-orange-400">
            정보통신망법 제44조의2에 따른 임시조치입니다. 만료일 이후 자동 복원되거나 관리자가 수동 해제할 수 있습니다.
          </p>
        </div>
      {/if}
      
      <!-- Moderation Info -->
      {#if post.moderated_at}
        <div class="p-6 border-t border-default bg-surface">
          <h3 class="text-label text-content-tertiary uppercase mb-3">모더레이션 정보</h3>
          <dl class="space-y-2 text-body2">
            <div class="flex">
              <dt class="text-content-tertiary w-24">처리자</dt>
              <dd class="text-content">{post.moderator?.name || '-'}</dd>
            </div>
            <div class="flex">
              <dt class="text-content-tertiary w-24">처리 시간</dt>
              <dd class="text-content">{new Date(post.moderated_at).toLocaleString('ko-KR')}</dd>
            </div>
            {#if post.moderation_note}
              <div class="flex">
                <dt class="text-content-tertiary w-24">사유</dt>
                <dd class="text-content">{post.moderation_note}</dd>
              </div>
            {/if}
          </dl>
        </div>
      {/if}
      
      <!-- AI Moderation -->
      {#if post.ai_moderation_result}
        <div class="p-6 border-t border-default">
          <h3 class="text-label text-content-tertiary uppercase mb-3">AI 모더레이션 결과</h3>
          <pre class="bg-surface-tertiary p-4 rounded-xl text-body2 text-gray-700 dark:text-dark-text-medium overflow-x-auto border border-default">{JSON.stringify(post.ai_moderation_result, null, 2)}</pre>
        </div>
      {/if}
    </div>
    
    <!-- Reports -->
    {#if reports.length > 0}
      <div class="card card-bordered overflow-hidden mb-6 shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content flex items-center gap-2">
            <svg class="w-5 h-5 text-error" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.34 16.5c-.77.833.192 2.5 1.732 2.5z"/>
            </svg>
            신고 내역 ({reports.length}건)
          </h2>
        </div>
        
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each reports as report}
            <div class="p-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-body1 font-medium text-content">{report.reason}</span>
                <span class={`badge ${report.status === 'pending' ? 'badge-warning' : 'badge-neutral'}`}>
                  {report.status}
                </span>
              </div>
              {#if report.description}
                <p class="text-body2 text-gray-700 dark:text-dark-text-medium mb-2">{report.description}</p>
              {/if}
              <div class="text-caption text-content-tertiary">
                신고자: {report.reporter?.name} | {new Date(report.created_at).toLocaleString('ko-KR')}
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
    
    <!-- Version History -->
    {#if versions.length > 0}
      <div class="card card-bordered overflow-hidden shadow-md">
        <div class="px-6 py-4 border-b border-default">
          <h2 class="text-title text-content">수정 이력</h2>
        </div>
        
        <div class="divide-y divide-gray-100 dark:divide-dark-border">
          {#each versions as version}
            <div class="px-6 py-4">
              <div class="flex items-center justify-between mb-2">
                <span class="text-content font-medium">{version.event}</span>
                <span class="text-caption text-content-tertiary">{new Date(version.created_at).toLocaleString('ko-KR')}</span>
              </div>
            </div>
          {/each}
        </div>
      </div>
    {/if}
    
    <!-- Back Button -->
    <div class="flex justify-center mt-8">
      <a
        href="/admin/posts"
        class="btn btn-secondary btn-md"
      >
        목록으로
      </a>
    </div>
  </main>
  
  <!-- Reject Modal -->
  {#if showRejectModal}
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="card card-bordered p-6 max-w-md w-full mx-4 shadow-xl">
        <h3 class="text-title text-content mb-2">게시글 반려</h3>
        <p class="text-body2 text-content-secondary mb-4">반려 사유를 입력해주세요.</p>
        
        <textarea
          bind:value={rejectNote}
          rows="3"
          placeholder="반려 사유..."
          class="textarea resize-none mb-4"
        ></textarea>
        
        <div class="flex justify-end gap-3">
          <button onclick={() => showRejectModal = false} class="btn btn-secondary btn-sm">취소</button>
          <button onclick={rejectPost} class="btn btn-danger btn-sm">반려</button>
        </div>
      </div>
    </div>
  {/if}

  <!-- Blind Modal (임시조치) -->
  {#if showBlindModal}
    <div class="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div class="card card-bordered p-6 max-w-lg w-full mx-4 shadow-xl">
        <h3 class="text-title text-content mb-1 flex items-center gap-2">
          <svg class="w-5 h-5 text-gray-700 dark:text-dark-text-high" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"/>
          </svg>
          임시 블라인드 처리
        </h3>
        <p class="text-body2 text-content-secondary mb-4">
          정보통신망법 제44조의2에 따른 임시조치입니다. 지정된 기간 동안 게시글이 비공개 처리됩니다.
        </p>

        <div class="space-y-4">
          <div>
            <label for="post-blind-reason" class="block text-label text-gray-700 dark:text-dark-text-medium mb-1">블라인드 사유 *</label>
            <textarea
              id="post-blind-reason"
              bind:value={blindReason}
              rows="2"
              placeholder="예: 권리침해 신고에 따른 임시조치"
              class="textarea resize-none"
            ></textarea>
          </div>

          <div>
            <label for="post-blind-duration" class="block text-label text-gray-700 dark:text-dark-text-medium mb-1">처리 기간</label>
            <select id="post-blind-duration" bind:value={blindDuration} class="select w-full">
              <option value={7}>7일</option>
              <option value={14}>14일</option>
              <option value={30}>30일 (기본)</option>
              <option value={60}>60일</option>
              <option value={90}>90일</option>
            </select>
          </div>

          <div>
            <label for="post-blind-requested-by" class="block text-label text-gray-700 dark:text-dark-text-medium mb-1">요청 유형</label>
            <select id="post-blind-requested-by" bind:value={blindRequestedBy} class="select w-full">
              <option value="admin">관리자 자체 판단</option>
              <option value="user">사용자 권리침해 신고</option>
              <option value="legal">법적 요청</option>
            </select>
          </div>
        </div>

        <div class="flex justify-end gap-3 mt-6">
          <button onclick={() => showBlindModal = false} class="btn btn-secondary btn-sm">취소</button>
          <button onclick={blindPost} class="btn btn-danger btn-sm">블라인드 처리</button>
        </div>
      </div>
    </div>
  {/if}
